import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { dataApi } from '../services/api';
import { useObfuscate } from '../hooks/useObfuscate';
import { Input } from './ui/input';
import { Search } from 'lucide-react';
import CustomerForm from '../pages/overlays/CustomerForm';
import VehicleForm from '../pages/overlays/VehicleForm';

/**
 * GlobalSearch - A reusable search box with popover results.
 * Prioritises results relevant to the current page context.
 * 
 * Props:
 * - onCustomerSelect(customer) - opens customer dialog instead of navigating
 * - onVehicleSelect(vehicle) - opens vehicle dialog instead of navigating
 */
const GlobalSearch = ({ className = '', onCustomerSelect, onVehicleSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ quoteRequests: [], customers: [], vehicles: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const priv = useObfuscate();

  // Internal dialog state for when no callbacks are provided
  const [internalCustomerId, setInternalCustomerId] = useState(null);
  const [internalCustomerOpen, setInternalCustomerOpen] = useState(false);
  const [internalVehicleId, setInternalVehicleId] = useState(null);
  const [internalVehicleOpen, setInternalVehicleOpen] = useState(false);

  // Determine current page context for result ordering
  const getPageContext = useCallback(() => {
    const path = location.pathname;
    if (path.includes('/quote-requests')) return 'quoteRequests';
    if (path.includes('/phonebook')) return 'customers';
    if (path.includes('/fleet')) return 'vehicles';
    return 'quoteRequests'; // default
  }, [location.pathname]);

  const performSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setResults({ quoteRequests: [], customers: [], vehicles: [] });
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      const [quoteRequests, customers, vehicles] = await Promise.all([
        dataApi.quoteRequestList({ search: searchTerm }).catch(() => []),
        dataApi.customerList({ search: searchTerm }).catch(() => []),
        dataApi.vehicleList().then(vList => {
          const s = searchTerm.toLowerCase();
          return (vList || []).filter(v => {
            const fields = [v.registration, v.rego, v.make, v.model, v.year?.toString()];
            return fields.some(f => f && f.toLowerCase().includes(s));
          });
        }).catch(() => [])
      ]);

      setResults({
        quoteRequests: (quoteRequests || []).slice(0, 5),
        customers: (customers || []).slice(0, 5),
        vehicles: (vehicles || []).slice(0, 5)
      });
      setOpen(true);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleSelect = (type, item) => {
    setOpen(false);
    setQuery('');
    setResults({ quoteRequests: [], customers: [], vehicles: [] });

    switch (type) {
      case 'quoteRequest':
        navigate(`/quote-requests/${item.$id}`);
        break;
      case 'customer':
        if (onCustomerSelect) {
          onCustomerSelect(item);
        } else {
          setInternalCustomerId(item.$id);
          setInternalCustomerOpen(true);
        }
        break;
      case 'vehicle':
        if (onVehicleSelect) {
          onVehicleSelect(item);
        } else {
          setInternalVehicleId(item.$id);
          setInternalVehicleOpen(true);
        }
        break;
      default:
        break;
    }
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const context = getPageContext();
  const hasResults = results.quoteRequests.length > 0 || results.customers.length > 0 || results.vehicles.length > 0;

  // Order sections by page context
  const sections = [
    { key: 'quoteRequests', label: 'Quote Requests', items: results.quoteRequests, type: 'quoteRequest' },
    { key: 'customers', label: 'Customers', items: results.customers, type: 'customer' },
    { key: 'vehicles', label: 'Vehicles', items: results.vehicles, type: 'vehicle' }
  ].sort((a, b) => {
    if (a.key === context) return -1;
    if (b.key === context) return 1;
    return 0;
  });

  const renderItem = (type, item) => {
    switch (type) {
      case 'quoteRequest': {
        const name = priv.name([item.customer?.firstName, item.customer?.lastName].filter(Boolean).join(' ')) || 'Unknown';
        const vehicle = [item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ');
        const pqNum = item.panelQuoteJobNumber;
        const label = pqNum ? `Panel Quote #${priv.jobNumber(pqNum)}` : `QR-${String(item.quoteRequestNumber || '0').padStart(5, '0')}`;
        return (
          <div
            key={item.$id}
            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent rounded-md transition-colors"
            onClick={() => handleSelect('quoteRequest', item)}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{name}</div>
              <div className="text-xs text-muted-foreground truncate">{label} · {vehicle}</div>
            </div>
          </div>
        );
      }
      case 'customer':
        return (
          <div
            key={item.$id}
            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent rounded-md transition-colors"
            onClick={() => handleSelect('customer', item)}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{priv.name([item.firstName, item.lastName].filter(Boolean).join(' '))}</div>
              <div className="text-xs text-muted-foreground truncate">{item.email ? priv.email(item.email, item.firstName) : (item.phone ? priv.phone(item.phone) : '')}</div>
            </div>
          </div>
        );
      case 'vehicle':
        return (
          <div
            key={item.$id}
            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent rounded-md transition-colors"
            onClick={() => handleSelect('vehicle', item)}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {[item.year, item.make, item.model].filter(Boolean).join(' ')}
              </div>
              <div className="text-xs text-muted-foreground truncate font-mono">
                {priv.rego(item.registration || item.rego) || ''}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search..."
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (hasResults) setOpen(true); }}
          className="pl-9 w-full bg-card shadow-sm"
        />
      </div>

      {open && (query.length >= 2) && (
        <div className="absolute top-full mt-1 w-full min-w-[320px] max-h-[400px] overflow-y-auto rounded-md border bg-card text-card-foreground shadow-lg z-50">
          {loading && !hasResults ? (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">Searching…</div>
          ) : !hasResults ? (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">No results found</div>
          ) : (
            <div className="py-1">
              {sections.map(section => {
                if (section.items.length === 0) return null;
                return (
                  <div key={section.key}>
                    <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {section.label}
                    </div>
                    {section.items.map(item => renderItem(section.type, item))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>

      {/* Internal dialogs for when no callbacks are provided */}
      {!onCustomerSelect && (
        <CustomerForm
          customerId={internalCustomerId}
          isOpen={internalCustomerOpen}
          onClose={() => { setInternalCustomerOpen(false); setInternalCustomerId(null); }}
        />
      )}
      {!onVehicleSelect && (
        <VehicleForm
          vehicleId={internalVehicleId}
          isOpen={internalVehicleOpen}
          onClose={() => { setInternalVehicleOpen(false); setInternalVehicleId(null); }}
        />
      )}
    </>
  );
};

export default GlobalSearch;
